import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip as MuiTooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  FormControlLabel,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import {
  getAzureMigrationPerformance,
  getMigrationSearch,
  getAzureMigrationExclusionConfig,
  updateAzureMigrationExclusionConfig
} from '../../services/apiService';
import {
  mergeDualSeries,
  pctDelta,
  verdictLowerBetter,
  verdictHigherBetter
} from './azureMigrationComparisonUtils';
import { getDeepDiveSections } from './azureMigrationMetricDeepDiveContent';
import { onMigrationDataUpdated } from './migrationRefreshEvents';
import {
  computeWeeklyStats,
  computeRootCauseHypotheses
} from './azureMigrationWeeklyStats';

const COLO_COLOR = '#D74B00';
const AZURE_COLOR = '#1D4434';
const GOOD = '#2e7d32';
const BAD = '#c62828';

/**
 * Splits Azure data points in a merged series into "early" vs "recent" (last
 * recentDays points) and returns the improvement % (positive = better).
 * Returns null when there is not enough data for a meaningful split.
 */
function seriesTrendImprovement(trendData, lowerBetter = true, recentDays = 7) {
  const pts = trendData
    .map((d) => d.azure)
    .filter((v) => v != null && Number.isFinite(v));
  if (pts.length < recentDays + 4) return null; // need a meaningful early window too
  const recent  = pts.slice(-recentDays);
  const earlier = pts.slice(0, -recentDays);
  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const rAvg = avg(recent);
  const eAvg = avg(earlier);
  if (!eAvg) return null;
  const rawPct = ((rAvg - eAvg) / Math.abs(eAvg)) * 100;
  return lowerBetter ? -rawPct : rawPct; // positive = improving
}

function formatTickTs(ts) {
  if (ts == null) return '';
  return format(new Date(ts), 'd MMM');
}

function withRollingTrend(data, windowSize = 8) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const avg = (arr) => {
    const vals = arr.filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  return data.map((row, i) => {
    const start = Math.max(0, i - windowSize + 1);
    const win = data.slice(start, i + 1);
    return {
      ...row,
      coloTrend: avg(win.map((r) => r.colo)),
      azureTrend: avg(win.map((r) => r.azure))
    };
  });
}

function pointLabelRenderer(formatter, every = 8) {
  return ({ x, y, value, index }) => {
    if (value == null || !Number.isFinite(value) || index % every !== 0) return null;
    return (
      <text x={x} y={y - 8} fontSize={10} fill="#666" textAnchor="middle">
        {formatter(value)}
      </text>
    );
  };
}

function fmtWithUnit(value, formatter) {
  if (value == null || !Number.isFinite(value)) return '—';
  return formatter(value);
}

function DurationTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = label != null ? format(new Date(label), 'd MMM yyyy HH:mm') : '';
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" display="block" color="text.secondary">
        {d}
      </Typography>
      {payload.map((p) => (
        <Typography key={p.dataKey} variant="body2">
          {p.name}:{' '}
          {p.value != null && Number.isFinite(p.value) ? `${(p.value * 1000).toFixed(1)} ms` : '—'}
        </Typography>
      ))}
    </Paper>
  );
}

function GenericTooltip({ active, payload, label, valueLabel }) {
  if (!active || !payload?.length) return null;
  const d = label != null ? format(new Date(label), 'd MMM yyyy HH:mm') : '';
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" display="block" color="text.secondary">
        {d}
      </Typography>
      {payload.map((p) => (
        <Typography key={p.dataKey} variant="body2">
          {p.name}:{' '}
          {p.value != null && Number.isFinite(p.value)
            ? typeof valueLabel === 'function'
              ? valueLabel(p.value)
              : p.value
            : '—'}
        </Typography>
      ))}
    </Paper>
  );
}

function ScorecardDetailPanel({ selectedKey, scorecardItems, meta, onClose }) {
  const item = scorecardItems.find((i) => i.key === selectedKey);
  const sections = item && selectedKey ? getDeepDiveSections(selectedKey, item) : null;
  if (!sections || !item || !meta) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'action.hover',
        borderLeft: 4,
        borderColor: AZURE_COLOR
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <InfoOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {sections.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Snapshot: {meta.oldEnv} {item.fmtColo} · {meta.newEnv} {item.fmtAzure}
              {item.deltaPct != null && Number.isFinite(item.deltaPct) && (
                <>
                  {' '}
                  · Δ {item.deltaPct > 0 ? '+' : ''}
                  {item.deltaPct.toFixed(1)}%
                </>
              )}{' '}
              · {item.verdict.label}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close details">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {sections.whatItMeasures}
      </Typography>

      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {sections.focusLabel}
      </Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {sections.focusBullets.map((line, i) => (
          <ListItem key={i} sx={{ py: 0.25, pl: 0, alignItems: 'flex-start' }}>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={line} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        What to check in Datadog
      </Typography>
      <List dense disablePadding>
        {sections.datadogChecks.map((line, i) => (
          <ListItem key={`dd-${i}`} sx={{ py: 0.25, pl: 0, alignItems: 'flex-start' }}>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              primary={line}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}

// ── Migration Verdict Banner ────────────────────────────────────────────────
function MigrationVerdictBanner({ scorecardItems, meta, recoveryTrend }) {
  const good = scorecardItems.filter((i) => i.verdict.tone === 'good');
  const bad  = scorecardItems.filter((i) => i.verdict.tone === 'bad');
  const avgImprove = good.length
    ? (good.reduce((s, i) => s + Math.abs(i.deltaPct ?? 0), 0) / good.length).toFixed(1)
    : 0;

  const tone =
    bad.length === 0             ? 'excellent'
    : bad.length <= 1            ? 'good'
    : good.length >= bad.length  ? 'mixed'
    :                              'concern';

  const cfg = {
    excellent: { bg: '#f1f8f2', border: GOOD,      label: 'EXCELLENT MIGRATION',  emoji: '✅' },
    good:      { bg: '#f1f8f2', border: GOOD,      label: 'PERFORMING WELL',      emoji: '✅' },
    mixed:     { bg: '#fffde7', border: '#f59e0b', label: 'MOSTLY POSITIVE',      emoji: '⚠️' },
    concern:   { bg: '#fff5f5', border: BAD,       label: 'NEEDS ATTENTION',      emoji: '🔴' },
  }[tone];

  const isRecovering = recoveryTrend?.direction === 'recovering' && (tone === 'concern' || tone === 'mixed');
  const isDegrading  = recoveryTrend?.direction === 'degrading'  && (tone === 'excellent' || tone === 'good');

  const migDate = meta.azureMigrationDate
    ? format(new Date(meta.azureMigrationDate), 'd MMM yyyy')
    : null;
  const daysOnAzure = meta.azureMigrationDate
    ? Math.floor((Date.now() - new Date(meta.azureMigrationDate).getTime()) / 86400000)
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.75, mb: 2.5, bgcolor: cfg.bg, borderColor: cfg.border, borderWidth: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={800} sx={{ color: cfg.border, whiteSpace: 'nowrap' }}>
          {cfg.emoji} {cfg.label}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${good.length}/${scorecardItems.length} metrics improved`}
            sx={{ bgcolor: 'success.light', color: GOOD, fontWeight: 700 }}
          />
          {Number(avgImprove) > 0 && (
            <Chip
              size="small"
              label={`avg ${avgImprove}% better on Azure`}
              sx={{ bgcolor: 'success.light', color: GOOD, fontWeight: 700 }}
            />
          )}
          {bad.length > 0 && (
            <Chip
              size="small"
              label={`${bad.length} regression${bad.length > 1 ? 's' : ''}`}
              sx={{ bgcolor: 'error.light', color: BAD, fontWeight: 700 }}
            />
          )}
          {isRecovering && (
            <MuiTooltip arrow title={`${recoveryTrend.improving} key metric${recoveryTrend.improving !== 1 ? 's' : ''} improved ≥8% in the last 7 days vs the earlier post-migration period. The 30-day average still looks worse because of the rocky start, but recent days are trending better.`}>
              <Chip
                size="small"
                label="↗ Recovering recently"
                sx={{ bgcolor: '#e0f7fa', color: '#006064', fontWeight: 700, border: '1px solid #80deea', cursor: 'help' }}
              />
            </MuiTooltip>
          )}
          {isDegrading && (
            <MuiTooltip arrow title={`Watch out — ${recoveryTrend.degrading} metric${recoveryTrend.degrading !== 1 ? 's' : ''} that were good overall are now worsening in the last 7 days.`}>
              <Chip
                size="small"
                label="↘ Degrading recently"
                sx={{ bgcolor: '#fff3e0', color: '#bf360c', fontWeight: 700, border: '1px solid #ffcc80', cursor: 'help' }}
              />
            </MuiTooltip>
          )}
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Box
            sx={{ px: 1, py: 0.25, borderRadius: 1, fontWeight: 700, fontSize: 12,
              bgcolor: '#fde8df', color: COLO_COLOR }}
          >
            {meta.oldEnv}
          </Box>
          <Typography variant="body2" color="text.secondary">→</Typography>
          <Box
            sx={{ px: 1, py: 0.25, borderRadius: 1, fontWeight: 700, fontSize: 12,
              bgcolor: '#d4eadf', color: AZURE_COLOR }}
          >
            {meta.newEnv}
          </Box>
          {migDate && (
            <Typography variant="caption" color="text.secondary">
              · Migrated {migDate}
              {daysOnAzure != null ? ` (${daysOnAzure}d on Azure)` : ''}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function buildResponseRows(split, apache) {
  const envData = apache?.[split];
  if (!envData) return [];
  const c = envData.colo  || {};
  const a = envData.azure || {};
  const msFmt = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`;
  };
  const cntFmt = (v) => (v != null && Number.isFinite(v) ? v.toLocaleString() : '—');
  const rows = [
    { label: 'Typical response time',  tooltip: 'Average — what most requests experience day-to-day', before: c.avgMs,  after: a.avgMs,  fmt: msFmt },
    { label: 'During busy periods',    tooltip: '95th percentile — 95% of requests are faster than this', before: c.p95Ms, after: a.p95Ms, fmt: msFmt },
    { label: 'Worst case',             tooltip: '99th percentile — the slowest 1 in 100 requests', before: c.p99Ms, after: a.p99Ms, fmt: msFmt },
    { label: 'Total requests handled', tooltip: 'Total number of requests in the comparison period', before: c.count, after: a.count, fmt: cntFmt, isCount: true },
  ];
  return rows.map((row) => {
    if (row.isCount) return { ...row, deltaPct: null, verdict: { tone: 'neutral', label: 'info' } };
    const d = pctDelta(row.before, row.after);
    const verdict = verdictLowerBetter(row.before, row.after, 10);
    return { ...row, deltaPct: d, verdict };
  });
}

function HealthCard({ label, question, item, meta }) {
  if (!item || !meta) return null;
  const isGood = item.verdict.tone === 'good';
  const isBad  = item.verdict.tone === 'bad';
  const pct = item.deltaPct != null && Number.isFinite(item.deltaPct)
    ? Math.abs(item.deltaPct).toFixed(0) : null;
  const cfg = isGood
    ? { emoji: '✅', color: GOOD,    bg: '#f0faf0', headline: pct ? `${pct}% better on Azure` : 'Improved' }
    : isBad
    ? { emoji: '⚠️', color: BAD,   bg: '#fff5f5', headline: pct ? `${pct}% worse on Azure` : 'Needs attention' }
    : { emoji: '✓',  color: '#666', bg: '#fafafa', headline: 'About the same' };
  return (
    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', height: '100%',
      borderTop: 3, borderColor: cfg.color, bgcolor: cfg.bg }}>
      <Typography sx={{ fontSize: 36, lineHeight: 1.1, mb: 0.75 }}>{cfg.emoji}</Typography>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>{label}</Typography>
      <Typography variant="caption" color="text.secondary" display="block"
        sx={{ mb: 2, minHeight: 34 }}>{question}</Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color: cfg.color, mb: 1.5 }}>
        {cfg.headline}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: '#fde8df',
          color: COLO_COLOR, fontWeight: 700, fontSize: 12 }}>
          Before&nbsp;{item.fmtColo}
        </Box>
        <Typography variant="caption" color="text.disabled">→</Typography>
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: '#d4eadf',
          color: AZURE_COLOR, fontWeight: 700, fontSize: 12 }}>
          After&nbsp;{item.fmtAzure}
        </Box>
      </Box>
    </Paper>
  );
}

function generateReportHtml({ meta, summary, scorecardItems, rootCauseHypotheses, topErrors, uiResponseRows, apiResponseRows }) {
  const BC = '#D74B00'; // Before colour
  const AC = '#1D4434'; // After colour
  const GOOD = '#2e7d32';
  const BAD  = '#c62828';
  const WARN = '#e65100';

  const vcol = (t) => t === 'good' ? GOOD : t === 'bad' ? BAD : '#666';
  const vbg  = (t) => t === 'good' ? '#e8f5e9' : t === 'bad' ? '#ffebee' : '#f0f0f0';

  const migDate = meta.azureMigrationDate
    ? new Date(meta.azureMigrationDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const daysOnAzure = meta.azureMigrationDate
    ? Math.floor((Date.now() - new Date(meta.azureMigrationDate).getTime()) / 86400000)
    : null;
  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const generated = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Overall verdict
  const goodCount    = scorecardItems.filter(it => it.verdict.tone === 'good').length;
  const badCount     = scorecardItems.filter(it => it.verdict.tone === 'bad').length;
  const neutralCount = scorecardItems.filter(it => it.verdict.tone === 'neutral').length;
  const overallTone  = badCount >= 3 ? 'bad' : badCount >= 1 ? 'watch' : 'good';
  const overallBg    = overallTone === 'good' ? '#f0faf0' : overallTone === 'bad' ? '#fff5f5' : '#fffde7';
  const overallBdr   = overallTone === 'good' ? GOOD : overallTone === 'bad' ? BAD : WARN;
  const overallTitle = overallTone === 'good' ? 'Migration Performing Well' : overallTone === 'bad' ? 'Action Required' : 'Broadly Stable — Monitor Closely';
  const overallBody  = overallTone === 'good'
    ? 'Key performance metrics have improved or remained stable since the migration to Azure.'
    : overallTone === 'bad'
    ? 'One or more critical metrics show regression. Review the details below and verify with your engineering team.'
    : 'Most metrics are stable. A small number of areas warrant closer observation over the coming weeks.';

  // Key KPI items
  const kpis = [
    scorecardItems.find(it => it.key === 'p99'),
    scorecardItems.find(it => it.key === 'apacheApi') || scorecardItems.find(it => it.key === 'http'),
    scorecardItems.find(it => it.key === 'err'),
    scorecardItems.find(it => it.key === 'mem'),
  ].filter(Boolean);

  const kpiCard = (it) => {
    const isGood = it.verdict.tone === 'good';
    const isBad  = it.verdict.tone === 'bad';
    const col    = vcol(it.verdict.tone);
    const bg     = vbg(it.verdict.tone);
    const bdr    = isGood ? '#a5d6a7' : isBad ? '#ef9a9a' : '#ddd';
    const isHigherBetter = it.key === 'mem';
    const arrow  = isHigherBetter
      ? (isGood ? '↑' : isBad ? '↓' : '≈')
      : (isGood ? '↓' : isBad ? '↑' : '≈');
    const pctStr = it.deltaPct != null && isFinite(it.deltaPct)
      ? `${it.deltaPct > 0 ? '+' : ''}${it.deltaPct.toFixed(1)}%` : '';
    const shortLabel = it.key === 'p99' ? 'App Response Time'
      : it.key === 'apacheApi' ? 'API Response Time'
      : it.key === 'http' ? 'Outbound Call Time'
      : it.key === 'err' ? 'Error Rate'
      : it.key === 'mem' ? 'Server Memory' : it.title.split(' — ')[0];
    return `
      <div style="border:1px solid ${bdr};border-radius:10px;padding:20px 16px;background:${bg};text-align:center;display:flex;flex-direction:column;gap:6px">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#777">${shortLabel}</div>
        <div style="display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap">
          <div>
            <div style="font-size:10px;color:${BC};font-weight:600;margin-bottom:3px">BEFORE</div>
            <div style="font-size:20px;font-weight:800;color:${BC}">${it.fmtColo}</div>
          </div>
          <div style="font-size:22px;font-weight:800;color:${col}">${arrow}</div>
          <div>
            <div style="font-size:10px;color:${AC};font-weight:600;margin-bottom:3px">AFTER</div>
            <div style="font-size:20px;font-weight:800;color:${AC}">${it.fmtAzure}</div>
          </div>
        </div>
        ${pctStr ? `<div style="font-size:15px;font-weight:700;color:${col}">${pctStr}</div>` : ''}
        <div style="display:inline-block;padding:3px 12px;border-radius:20px;background:rgba(255,255,255,.6);color:${col};font-size:11px;font-weight:700;border:1px solid ${bdr}">${it.verdict.label}</div>
      </div>`;
  };

  const barRow = (r, idx) => {
    const pct  = r.deltaPct;
    const tone = r.verdict?.tone || 'neutral';
    const col  = vcol(tone);
    const barW = Math.min(100, Math.abs(pct || 0));
    const even = idx % 2 === 0;
    return `
      <tr style="background:${even ? '#fff' : '#fafafa'}">
        <td style="padding:10px 14px;width:28%">
          <div style="font-weight:600;color:#333;font-size:13px">${r.label}</div>
          <div style="font-size:11px;color:#aaa;margin-top:2px">${r.tooltip}</div>
        </td>
        <td style="padding:10px 14px;text-align:right;font-weight:700;color:${BC};font-size:14px;white-space:nowrap">${r.fmt(r.before)}</td>
        <td style="padding:10px 14px;min-width:150px">
          <div style="display:flex;align-items:center;gap:7px">
            <div style="flex:1;height:6px;background:#eee;border-radius:3px;overflow:hidden">
              <div style="height:6px;width:${barW}%;background:${col};border-radius:3px"></div>
            </div>
            <span style="font-size:12px;font-weight:700;color:${col};min-width:46px;text-align:right">${pct != null && isFinite(pct) ? (pct > 0 ? '+' : '') + pct.toFixed(1) + '%' : '—'}</span>
          </div>
        </td>
        <td style="padding:10px 14px;text-align:right;font-weight:700;color:${AC};font-size:14px;white-space:nowrap">${r.fmt(r.after)}</td>
      </tr>`;
  };

  const responseBlock = (rows, title, subtitle, accent) => {
    if (!rows || rows.length === 0) return '';
    const countRow = rows.find(r => r.isCount);
    const dataRows = rows.filter(r => !r.isCount);
    return `
      <div style="margin-bottom:28px">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
          <div style="width:4px;height:22px;background:${accent};border-radius:2px;flex-shrink:0"></div>
          <div>
            <div style="font-size:15px;font-weight:700;color:#222">${title}</div>
            <div style="font-size:12px;color:#888;margin-top:1px">${subtitle}</div>
          </div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead>
            <tr style="background:#f6f7f6">
              <th style="padding:9px 14px;text-align:left;font-size:11px;font-weight:700;color:#666;text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #e8e8e8;width:28%">Measure</th>
              <th style="padding:9px 14px;text-align:right;font-size:11px;font-weight:700;color:${BC};text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #e8e8e8">Before (${meta.oldEnv})</th>
              <th style="padding:9px 14px;font-size:11px;font-weight:700;color:#666;text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #e8e8e8;min-width:150px">Change</th>
              <th style="padding:9px 14px;text-align:right;font-size:11px;font-weight:700;color:${AC};text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #e8e8e8">After (${meta.newEnv})</th>
            </tr>
          </thead>
          <tbody>
            ${dataRows.map((r, i) => barRow(r, i)).join('')}
            ${countRow ? `
            <tr style="background:#f9f9f9">
              <td style="padding:8px 14px;font-size:12px;color:#999">Total requests handled</td>
              <td style="padding:8px 14px;text-align:right;font-size:12px;color:${BC};font-weight:600">${countRow.before != null ? Number(countRow.before).toLocaleString() : '—'}</td>
              <td style="padding:8px 14px;font-size:12px;color:#ccc">—</td>
              <td style="padding:8px 14px;text-align:right;font-size:12px;color:${AC};font-weight:600">${countRow.after != null ? Number(countRow.after).toLocaleString() : '—'}</td>
            </tr>` : ''}
          </tbody>
        </table>
      </div>`;
  };

  const hypothesisCard = (h) => {
    const bdr   = h.tone === 'bad' ? BAD : h.tone === 'watch' ? WARN : h.tone === 'neutral' ? '#9e9e9e' : AC;
    const pillBg = h.tone === 'bad' ? '#ffebee' : h.tone === 'watch' ? '#fff3e0' : h.tone === 'neutral' ? '#f5f5f5' : '#e8f5e9';
    const pillC  = h.tone === 'bad' ? BAD : h.tone === 'watch' ? WARN : h.tone === 'neutral' ? '#666' : GOOD;
    return `
      <div style="border:1px solid #e4e4e4;border-left:4px solid ${bdr};border-radius:8px;padding:20px;margin-bottom:14px;background:#fff;page-break-inside:avoid">
        <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px;flex-wrap:wrap">
          <div style="flex:1">
            <div style="font-size:15px;font-weight:700;color:#222;margin-bottom:6px">${h.label}</div>
            <span style="display:inline-block;padding:2px 10px;border-radius:20px;background:${pillBg};color:${pillC};font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em">${h.confidence} confidence</span>
          </div>
        </div>
        <p style="margin:0 0 14px;color:#555;font-size:13px;line-height:1.65">${h.summary}</p>
        ${h.evidence?.length ? `
          <div style="margin-bottom:12px">
            <div style="font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">Evidence</div>
            <ul style="margin:0;padding-left:18px">
              ${h.evidence.map(e => `<li style="font-size:13px;color:#555;margin-bottom:5px;line-height:1.5">${e}</li>`).join('')}
            </ul>
          </div>` : ''}
        ${h.nextSteps?.length ? `
          <div>
            <div style="font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">Where to investigate</div>
            <ul style="margin:0;padding-left:18px">
              ${h.nextSteps.map(s => `<li style="font-size:13px;color:#444;margin-bottom:5px;line-height:1.5">${s}</li>`).join('')}
            </ul>
          </div>` : ''}
      </div>`;
  };

  const errItem = scorecardItems.find(it => it.key === 'err');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Migration Report — ${meta.instanceName || 'Report'}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif; color: #333; background: #eef4fb; line-height: 1.5; }
  .page { max-width: 980px; margin: 0 auto; background: #fff; box-shadow: 0 4px 24px rgba(30,70,120,.10); }
  .report-header { background: linear-gradient(130deg, #ddeeff 0%, #c8e0f8 100%); color: #1a2e45; padding: 44px 52px 38px; border-bottom: 2px solid #b3cfe8; }
  .header-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #5b85aa; margin-bottom: 20px; }
  .header-title { font-size: 28px; font-weight: 800; color: #1a2e45; margin-bottom: 6px; line-height: 1.2; }
  .header-url { font-size: 13px; color: #6a8faf; margin-bottom: 22px; }
  .header-badges { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 20px; }
  .hb { display: inline-block; padding: 5px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; border: 1px solid transparent; }
  .hb-before { background: #ffeee0; color: #c44200; border-color: #f5c4a0; }
  .hb-after  { background: #d8ede3; color: #1D4434; border-color: #a8d0bb; }
  .hb-info   { background: rgba(26,46,69,.07); color: #3a5a7a; border-color: rgba(26,46,69,.15); }
  .header-period { font-size: 12px; color: #6a8faf; }
  .content { padding: 44px 52px; }
  .section { margin-bottom: 44px; }
  .section-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em; color: #999; padding-bottom: 10px; border-bottom: 1px solid #eee; margin-bottom: 20px; }
  .verdict-banner { border-radius: 10px; padding: 20px 24px; display: flex; align-items: flex-start; gap: 16px; margin-bottom: 28px; }
  .v-icon { font-size: 26px; line-height: 1; flex-shrink: 0; margin-top: 2px; }
  .v-title { font-size: 17px; font-weight: 700; margin-bottom: 5px; }
  .v-body { font-size: 13px; line-height: 1.65; color: #555; margin-bottom: 12px; }
  .v-pills { display: flex; gap: 8px; flex-wrap: wrap; }
  .vpill { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }
  .vpill-good { background: #e8f5e9; color: ${GOOD}; }
  .vpill-bad  { background: #ffebee; color: ${BAD}; }
  .vpill-neut { background: #f0f0f0; color: #666; }
  .kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 8px; }
  .chip-new     { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; background: #ffebee; color: ${BAD}; border: 1px solid #ffcdd2; margin: 3px 4px 3px 0; }
  .chip-cleared { display: inline-block; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; background: #e8f5e9; color: ${GOOD}; border: 1px solid #c8e6c9; margin: 3px 4px 3px 0; }
  .appendix-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .appendix-table thead th { padding: 9px 12px; background: #f6f7f6; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #666; border-bottom: 2px solid #e0e0e0; }
  .appendix-table tbody td { padding: 9px 12px; border-bottom: 1px solid #f4f4f4; }
  .appendix-table tbody tr:hover { background: #fafafa; }
  .vchip { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 700; }
  .report-footer { background: #f6f7f8; border-top: 1px solid #e8e8e8; padding: 18px 52px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 11px; color: #bbb; }
  .page-divider { height: 1px; background: linear-gradient(90deg, transparent, #e0e0e0 20%, #e0e0e0 80%, transparent); margin: 0 0 40px; }
  @media print {
    body { background: #fff; }
    .page { box-shadow: none; max-width: 100%; }
    .content { padding: 28px 36px; }
    .report-header { padding: 28px 36px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .kpi-row { page-break-inside: avoid; }
    .section { page-break-inside: avoid; }
    .verdict-banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  @media (max-width: 680px) {
    .report-header, .content { padding: 24px 20px; }
    .kpi-row { grid-template-columns: 1fr 1fr; }
    .report-footer { padding: 16px 20px; }
  }
</style>
</head>
<body>
<div class="page">

  <!-- ═══ HEADER ═══ -->
  <div class="report-header">
    <div class="header-eyebrow">HighQ · Azure Migration Performance Report</div>
    <h1 class="header-title">${meta.instanceName || 'Migration Report'}</h1>
    ${meta.instanceUrl ? `<div class="header-url">${meta.instanceUrl}</div>` : ''}
    <div class="header-badges">
      <span class="hb hb-before">Before: ${meta.oldEnv}</span>
      <span style="color:#9ab8d4;font-size:18px;line-height:1">→</span>
      <span class="hb hb-after">After: ${meta.newEnv}</span>
      ${migDate ? `<span class="hb hb-info">Migrated ${migDate}</span>` : ''}
      ${daysOnAzure != null ? `<span class="hb hb-info">${daysOnAzure} days since migration</span>` : ''}
    </div>
    <div class="header-period">
      Report period: ${fmtDate(meta.from)} – ${fmtDate(meta.to)} &nbsp;·&nbsp; Generated ${generated}
    </div>
  </div>

  <div class="content">

    <!-- ═══ EXECUTIVE SUMMARY ═══ -->
    <div class="section">
      <div class="section-label">Executive Summary</div>

      <div class="verdict-banner" style="background:${overallBg};border:1px solid ${overallBdr}30;border-left:4px solid ${overallBdr}">
        <div class="v-icon" style="color:${overallBdr}">${overallTone === 'good' ? '✔' : overallTone === 'bad' ? '⚠' : '●'}</div>
        <div>
          <div class="v-title" style="color:${overallBdr}">${overallTitle}</div>
          <div class="v-body">${overallBody}</div>
          <div class="v-pills">
            ${goodCount    > 0 ? `<span class="vpill vpill-good">✓ ${goodCount} metric${goodCount > 1 ? 's' : ''} improved</span>` : ''}
            ${badCount     > 0 ? `<span class="vpill vpill-bad">⚠ ${badCount} metric${badCount > 1 ? 's' : ''} need attention</span>` : ''}
            ${neutralCount > 0 ? `<span class="vpill vpill-neut">≈ ${neutralCount} metric${neutralCount > 1 ? 's' : ''} unchanged</span>` : ''}
          </div>
        </div>
      </div>

      <!-- KPI cards -->
      <div class="kpi-row">
        ${kpis.map(kpiCard).join('')}
      </div>
    </div>

    ${(uiResponseRows.length > 0 || apiResponseRows.length > 0) ? `
    <!-- ═══ RESPONSE TIMES ═══ -->
    <div class="section">
      <div class="section-label">Response Times — Real User Measurements</div>
      <p style="font-size:13px;color:#666;margin-bottom:24px;line-height:1.65">
        Measured at the Apache server edge — includes network, TLS and proxy overhead, so this reflects what users
        and integrations actually experience. Lower is better for all time metrics.
      </p>
      ${responseBlock(apiResponseRows, 'API Requests', 'Integrations, mobile apps, and service-to-service calls', AC)}
      ${responseBlock(uiResponseRows,  'Browser / UI Requests', 'Page loads and user-facing interactions', BC)}
    </div>` : ''}

    ${summary ? `
    <!-- ═══ ERROR ANALYSIS ═══ -->
    <div class="section">
      <div class="section-label">Error Analysis</div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;margin-bottom:20px">
        <div style="border:1px solid #e0e0e0;border-left:4px solid ${BC};border-radius:8px;padding:18px;text-align:center;background:#fff">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#888;margin-bottom:8px">Before — Errors / Day</div>
          <div style="font-size:32px;font-weight:800;color:${BC};margin-bottom:4px">${summary.colo.errorsPerDay != null ? Math.round(summary.colo.errorsPerDay).toLocaleString() : '—'}</div>
          ${summary.colo.errorRatePct != null ? `<div style="font-size:12px;color:#aaa">${summary.colo.errorRatePct.toFixed(3)}% rate · ${summary.colo.activeDays ?? '—'} days</div>` : ''}
        </div>
        <div style="border:1px solid #e0e0e0;border-left:4px solid ${AC};border-radius:8px;padding:18px;text-align:center;background:#fff">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#888;margin-bottom:8px">After — Errors / Day</div>
          <div style="font-size:32px;font-weight:800;color:${AC};margin-bottom:4px">${summary.azure.errorsPerDay != null ? Math.round(summary.azure.errorsPerDay).toLocaleString() : '—'}</div>
          ${summary.azure.errorRatePct != null ? `<div style="font-size:12px;color:#aaa">${summary.azure.errorRatePct.toFixed(3)}% rate · ${summary.azure.activeDays ?? '—'} days</div>` : ''}
        </div>
        ${errItem && errItem.deltaPct != null && isFinite(errItem.deltaPct) ? `
        <div style="border:1px solid ${errItem.verdict.tone === 'good' ? '#a5d6a7' : errItem.verdict.tone === 'bad' ? '#ef9a9a' : '#e0e0e0'};border-radius:8px;padding:18px;text-align:center;background:${vbg(errItem.verdict.tone)}">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#888;margin-bottom:8px">Change</div>
          <div style="font-size:32px;font-weight:800;color:${vcol(errItem.verdict.tone)};margin-bottom:6px">${errItem.deltaPct > 0 ? '+' : ''}${errItem.deltaPct.toFixed(1)}%</div>
          <span class="vchip" style="background:rgba(255,255,255,.7);color:${vcol(errItem.verdict.tone)};border:1px solid ${vcol(errItem.verdict.tone)}40">${errItem.verdict.label}</span>
        </div>` : '<div></div>'}
      </div>

      ${topErrors?.comparison && (topErrors.comparison.newInAzureByResource?.length > 0 || topErrors.comparison.clearedInAzureByResource?.length > 0) ? `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:4px">
        ${topErrors.comparison.newInAzureByResource?.length > 0 ? `
        <div style="border:1px solid #ffcdd2;border-radius:8px;padding:18px;background:#fff8f8">
          <div style="font-size:13px;font-weight:700;color:${BAD};margin-bottom:12px;display:flex;align-items:center;gap:7px">
            <span style="display:inline-block;width:8px;height:8px;background:${BAD};border-radius:50%;flex-shrink:0"></span>
            New after migration
            <span style="background:#ffebee;color:${BAD};padding:1px 8px;border-radius:12px;font-size:11px;margin-left:auto">${topErrors.comparison.newInAzureByResource.length}</span>
          </div>
          <div>
            ${topErrors.comparison.newInAzureByResource.slice(0, 12).map(r =>
              `<span class="chip-new">${r.label.length > 44 ? r.label.slice(0, 44) + '…' : r.label} <span style="opacity:.65;font-size:10px">${r.count}</span></span>`
            ).join('')}
            ${topErrors.comparison.newInAzureByResource.length > 12 ? `<span style="font-size:11px;color:#aaa;padding:4px">+${topErrors.comparison.newInAzureByResource.length - 12} more</span>` : ''}
          </div>
        </div>` : '<div></div>'}
        ${topErrors.comparison.clearedInAzureByResource?.length > 0 ? `
        <div style="border:1px solid #c8e6c9;border-radius:8px;padding:18px;background:#f6fff6">
          <div style="font-size:13px;font-weight:700;color:${GOOD};margin-bottom:12px;display:flex;align-items:center;gap:7px">
            <span style="display:inline-block;width:8px;height:8px;background:${GOOD};border-radius:50%;flex-shrink:0"></span>
            Resolved by migration
            <span style="background:#e8f5e9;color:${GOOD};padding:1px 8px;border-radius:12px;font-size:11px;margin-left:auto">${topErrors.comparison.clearedInAzureByResource.length}</span>
          </div>
          <div>
            ${topErrors.comparison.clearedInAzureByResource.slice(0, 12).map(r =>
              `<span class="chip-cleared">${r.label.length > 44 ? r.label.slice(0, 44) + '…' : r.label} <span style="opacity:.65;font-size:10px">${r.count}</span></span>`
            ).join('')}
            ${topErrors.comparison.clearedInAzureByResource.length > 12 ? `<span style="font-size:11px;color:#aaa;padding:4px">+${topErrors.comparison.clearedInAzureByResource.length - 12} more</span>` : ''}
          </div>
        </div>` : '<div></div>'}
      </div>
      ${topErrors.comparison.caveat ? `<p style="font-size:12px;color:#bbb;margin-top:12px;font-style:italic">${topErrors.comparison.caveat}</p>` : ''}` : ''}
    </div>` : ''}

    ${rootCauseHypotheses.length > 0 ? `
    <!-- ═══ ROOT CAUSE ═══ -->
    <div class="section">
      <div class="section-label">Root Cause Analysis</div>
      <p style="font-size:13px;color:#666;margin-bottom:20px;line-height:1.65">
        Automated correlation of weekly performance signals — latency, error rate, outbound calls, and memory —
        surfaces the most likely explanations for observed changes. Ranked by confidence.
      </p>
      ${rootCauseHypotheses.map(hypothesisCard).join('')}
    </div>` : ''}

    <!-- ═══ FULL METRICS APPENDIX ═══ -->
    <div class="section">
      <div class="section-label">Full Metrics — Appendix</div>
      <table class="appendix-table">
        <thead>
          <tr>
            <th style="width:38%;text-align:left">Metric</th>
            <th style="text-align:right;color:${BC}">Before (${meta.oldEnv})</th>
            <th style="text-align:center">Change</th>
            <th style="text-align:right;color:${AC}">After (${meta.newEnv})</th>
            <th style="text-align:center">Verdict</th>
          </tr>
        </thead>
        <tbody>
          ${scorecardItems.map((it, i) => {
            const isGood = it.verdict.tone === 'good';
            const isBad  = it.verdict.tone === 'bad';
            const col    = vcol(it.verdict.tone);
            const bg     = vbg(it.verdict.tone);
            const delta  = it.deltaPct != null && isFinite(it.deltaPct)
              ? `<span style="padding:2px 7px;border-radius:4px;background:${bg};color:${col};font-weight:700">${it.deltaPct > 0 ? '+' : ''}${it.deltaPct.toFixed(1)}%</span>`
              : '<span style="color:#ccc">—</span>';
            return `<tr style="${i % 2 === 0 ? '' : 'background:#fafafa'}">
              <td>
                <div style="font-weight:600;font-size:12px;color:#333">${it.title}</div>
                ${it.subColo ? `<div style="font-size:10px;color:#bbb;margin-top:1px">${it.subColo}</div>` : ''}
              </td>
              <td style="text-align:right;font-weight:700;color:${BC};font-size:13px">${it.fmtColo}</td>
              <td style="text-align:center">${delta}</td>
              <td style="text-align:right;font-weight:700;color:${AC};font-size:13px">${it.fmtAzure}</td>
              <td style="text-align:center">
                <span class="vchip" style="background:${bg};color:${col}">${it.verdict.label}</span>
              </td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>
    </div>

  </div><!-- /content -->

  <div class="report-footer">
    <span>HighQ Metrics Portal &nbsp;·&nbsp; Azure Migration Performance Report</span>
    <span>Generated ${generated} &nbsp;·&nbsp; ${meta.instanceName || 'unknown instance'}</span>
  </div>

</div><!-- /page -->
</body>
</html>`;
}

function generateWeekendDates(fromIso, toIso) {
  const dates = [];
  const cursor = new Date(fromIso);
  cursor.setUTCHours(0, 0, 0, 0);
  const end = new Date(toIso);
  while (cursor <= end) {
    const day = cursor.getUTCDay();
    if (day === 0 || day === 6) {
      dates.push(cursor.toISOString().slice(0, 10));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

const DD_BASE = 'https://tr-highq-prod.datadoghq.com';

function buildDdTracesUrl({ service, env, fromMs, toMs, resource } = {}) {
  let query = `service:${service} env:${env} status:error`;
  if (resource) query += ` resource_name:"${resource}"`;
  const params = new URLSearchParams({
    query,
    paused: 'false',
    ...(fromMs != null ? { from_ts: String(fromMs) } : {}),
    ...(toMs != null ? { to_ts: String(toMs) } : {}),
  });
  return `${DD_BASE}/apm/traces?${params.toString()}`;
}

/**
 * Azure vs Colo Datadog performance for a client (latest migration upload + env tags from sheet).
 */
const AzureMigrationPerformanceTab = () => {
  const [clientQuery, setClientQuery] = useState('');
  // Editable parameter overrides. All start empty — when empty, the backend auto-resolves
  // them from the migration sheet (source/current datacenter, default service). After a
  // successful load we prefill any blanks with the resolved meta so the edit panel shows
  // the values the report is currently using.
  const [service, setService] = useState('');
  const [oldEnv, setOldEnv] = useState('');
  const [newEnv, setNewEnv] = useState('');
  const [migrationDateInput, setMigrationDateInput] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [lastLoadedClient, setLastLoadedClient] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedScorecardKey, setSelectedScorecardKey] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [recentInstances, setRecentInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [activeResponseTab, setActiveResponseTab] = useState('ui');
  const [trendMetricKey, setTrendMetricKey] = useState('p99');
  const [excludedResources, setExcludedResources] = useState([]);
  const [excludeInput, setExcludeInput] = useState('');
  const [excludedDays, setExcludedDays] = useState([]);
  const [excludeDayInput, setExcludeDayInput] = useState('');
  const [weekdaysOnly, setWeekdaysOnly] = useState(false);
  const [ignoredMeta, setIgnoredMeta] = useState(null);
  const [exclusionModalOpen, setExclusionModalOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState([]);
  const [modalInput, setModalInput] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    setSelectedScorecardKey(null);
    setSelectedPoint(null);
  }, [payload]);

  // Fetch instances migrated within the last 30 days for the dropdown.
  // The server does the date + azureMigrated filter and orders by migration
  // date desc; previously we asked for the first 200 alphabetical rows and
  // filtered client-side, which silently dropped recently-migrated instances
  // whose instanceName fell after the 200th letter-sorted row.
  const RECENT_WINDOW_DAYS = 30;
  const RECENT_DROPDOWN_LIMIT = 500;

  const loadRecentInstances = useCallback(async (signal) => {
    setInstancesLoading(true);
    try {
      const res = await getMigrationSearch('', RECENT_DROPDOWN_LIMIT, {
        azureMigratedSinceDays: RECENT_WINDOW_DAYS
      });
      if (signal?.cancelled) return;
      if (res?.success) {
        // Server already filters + sorts; still guard against stale dates from
        // older backends that don't honour the new query parameter.
        const cutoff = subDays(new Date(), RECENT_WINDOW_DAYS).getTime();
        const recent = (res.data || [])
          .filter(
            (it) =>
              it.azureMigrated &&
              it.azureMigrationDate &&
              new Date(it.azureMigrationDate).getTime() >= cutoff
          )
          .sort((a, b) => new Date(b.azureMigrationDate) - new Date(a.azureMigrationDate));
        setRecentInstances(recent);
      }
    } catch {
      // dropdown is optional — ignore errors silently
    } finally {
      if (!signal?.cancelled) setInstancesLoading(false);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    loadRecentInstances(signal);
    return () => { signal.cancelled = true; };
  }, [loadRecentInstances]);

  // Re-fetch the dropdown list whenever the migration sheet is re-uploaded
  useEffect(
    () =>
      onMigrationDataUpdated(() => {
        const signal = { cancelled: false };
        loadRecentInstances(signal);
        return () => { signal.cancelled = true; };
      }),
    [loadRecentInstances]
  );

  // Load persisted global exclusions on mount and initialise local state from them
  useEffect(() => {
    let cancelled = false;
    getAzureMigrationExclusionConfig()
      .then((res) => {
        if (cancelled) return;
        const names = res?.data?.resourceNames ?? [];
        setExcludedResources(names);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buildPointClickDot = useCallback(
    (metricLabel, formatter, strokeColor) =>
      (props) => {
        const { cx, cy, payload: pointPayload } = props || {};
        if (cx == null || cy == null) return null;
        return (
          <circle
            cx={cx}
            cy={cy}
            r={3}
            fill="#fff"
            stroke={strokeColor}
            strokeWidth={1.5}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              setSelectedPoint({
                metricLabel,
                formatter,
                point: pointPayload
              })
            }
          />
        );
      },
    []
  );

  /**
   * Run the report. Pass `overrides` to bypass the current draft state (used by the
   * "Reset overrides" button so the backend auto-resolves source / current datacenter
   * and service from the migration sheet again).
   */
  const runReport = useCallback(
    async (overrides) => {
      // queryOverride lets callers (e.g. the dropdown onChange) pass the selected
      // value directly, bypassing the React state that may not have flushed yet.
      const q = (overrides?.queryOverride ?? clientQuery).trim();
      if (!q) {
        setError('Enter a client or instance name to search.');
        return;
      }

      // Values the backend will actually see for this run.
      const useReset = overrides?.forceReset === true;
      const clientChanged = q !== lastLoadedClient;
      const shouldClearDrafts = useReset || clientChanged;

      const appliedService = shouldClearDrafts ? '' : service.trim();
      const appliedOldEnv = shouldClearDrafts ? '' : oldEnv.trim();
      const appliedNewEnv = shouldClearDrafts ? '' : newEnv.trim();
      const appliedMigrationDate = shouldClearDrafts ? '' : migrationDateInput.trim();

      if (shouldClearDrafts) {
        setService('');
        setOldEnv('');
        setNewEnv('');
        setMigrationDateInput('');
      }

      setLoading(true);
      setError(null);
      setPayload(null);
      setIgnoredMeta(null);
      try {
        const activeExclusions = overrides?.exclusionsOverride ?? excludedResources;
        let activeDayExclusions = overrides?.dayExclusionsOverride ?? excludedDays;
        if (weekdaysOnly) {
          const toDate = new Date();
          const fromDate = subDays(toDate, 30);
          const weekendDates = generateWeekendDates(
            fromDate.toISOString().slice(0, 10),
            toDate.toISOString().slice(0, 10)
          );
          const combined = Array.from(new Set([...activeDayExclusions, ...weekendDates]));
          activeDayExclusions = combined;
        }
        const params = { q, days: 30 };
        if (appliedService) params.service = appliedService;
        if (appliedOldEnv) params.oldEnv = appliedOldEnv;
        if (appliedNewEnv) params.newEnv = appliedNewEnv;
        if (activeExclusions.length) params.excludeResources = activeExclusions.join(',');
        if (activeDayExclusions.length) params.excludeDays = activeDayExclusions.join(',');
        const res = await getAzureMigrationPerformance(params);
        if (res?.success) {
          if (res.ignored) {
            setIgnoredMeta(res.meta || {});
            setLastLoadedClient(q);
          } else {
            setPayload(res);
            setLastLoadedClient(q);
            const m = res.meta || {};
            // Prefill any empty drafts with the values the report is currently using so
            // the edit panel shows the effective configuration, not an empty form.
            if (!appliedService && m.service) setService(m.service);
            if (!appliedOldEnv && m.oldEnv) setOldEnv(m.oldEnv);
            if (!appliedNewEnv && m.newEnv) setNewEnv(m.newEnv);
            if (!appliedMigrationDate && m.azureMigrationDate) {
              setMigrationDateInput(String(m.azureMigrationDate).slice(0, 10));
            }
          }
        } else {
          setError(res?.message || 'Failed to load metrics');
        }
      } catch (e) {
        setError(e.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    },
    [clientQuery, service, oldEnv, newEnv, migrationDateInput, lastLoadedClient, excludedResources, excludedDays, weekdaysOnly]
  );

  const load = useCallback(() => runReport(), [runReport]);
  const resetOverridesAndReload = useCallback(() => runReport({ forceReset: true }), [runReport]);

  const weekdaysOnlyMounted = React.useRef(false);
  useEffect(() => {
    if (!weekdaysOnlyMounted.current) { weekdaysOnlyMounted.current = true; return; }
    if (lastLoadedClient) runReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekdaysOnly]);

  const addExclusion = useCallback(() => {
    const trimmed = excludeInput.trim();
    if (!trimmed) return;
    const next = excludedResources.includes(trimmed)
      ? excludedResources
      : [...excludedResources, trimmed];
    setExcludedResources(next);
    setExcludeInput('');
    if (lastLoadedClient) runReport({ exclusionsOverride: next });
  }, [excludeInput, excludedResources, lastLoadedClient, runReport]);

  const addExcludeDay = useCallback(() => {
    const d = excludeDayInput.trim();
    if (!d) return;
    const next = excludedDays.includes(d) ? excludedDays : [...excludedDays, d].sort();
    setExcludedDays(next);
    setExcludeDayInput('');
    if (lastLoadedClient) runReport({ dayExclusionsOverride: next });
  }, [excludeDayInput, excludedDays, lastLoadedClient, runReport]);

  const removeExcludeDay = useCallback((day) => {
    const next = excludedDays.filter((d) => d !== day);
    setExcludedDays(next);
    if (lastLoadedClient) runReport({ dayExclusionsOverride: next });
  }, [excludedDays, lastLoadedClient, runReport]);

  const removeExclusion = useCallback((name) => {
    const next = excludedResources.filter((r) => r !== name);
    setExcludedResources(next);
    if (lastLoadedClient) runReport({ exclusionsOverride: next });
  }, [excludedResources, lastLoadedClient, runReport]);

  // When the migration Excel is re-uploaded we may have new source/target datacenters
  // or a new migration date for the currently-loaded client. Re-run the report from
  // scratch (resetting any manual overrides) so the fresh sheet values get picked up.
  useEffect(
    () =>
      onMigrationDataUpdated(() => {
        if (lastLoadedClient) resetOverridesAndReload();
      }),
    [lastLoadedClient, resetOverridesAndReload]
  );

  /**
   * effectiveMeta overlays the user's client-side migration-date edit onto the payload
   * meta. The backend doesn't consume `azureMigrationDate` for its Datadog queries, so
   * overriding it purely in the UI updates the cutover marker and the weekly pre/post
   * bucketing without requiring a reload.
   */
  const effectiveMeta = useMemo(() => {
    if (!payload?.meta) return null;
    const m = { ...payload.meta };
    const originalDate = m.azureMigrationDate ? String(m.azureMigrationDate).slice(0, 10) : '';
    const draftDate = migrationDateInput.trim();
    if (draftDate && draftDate !== originalDate) {
      const parsed = new Date(`${draftDate}T00:00:00Z`);
      if (!Number.isNaN(parsed.getTime())) {
        m.azureMigrationDate = parsed.toISOString();
      }
    }
    return m;
  }, [payload, migrationDateInput]);

  const meta = effectiveMeta;
  const series = payload?.series;
  const summary = payload?.summary;
  const insights = payload?.insights;
  const topErrors = payload?.topErrors;
  const apache = payload?.apache;

  const httpData = useMemo(
    () => mergeDualSeries(series?.httpClientDuration?.colo, series?.httpClientDuration?.azure),
    [series]
  );
  const p99Data = useMemo(
    () => mergeDualSeries(series?.servletLatencyP99?.colo, series?.servletLatencyP99?.azure),
    [series]
  );
  const errData = useMemo(
    () => mergeDualSeries(series?.errorRate?.colo, series?.errorRate?.azure),
    [series]
  );
  const memData = useMemo(
    () => mergeDualSeries(series?.hostMemPctUsable?.colo, series?.hostMemPctUsable?.azure),
    [series]
  );

  const httpTrendData = useMemo(() => withRollingTrend(httpData, 10), [httpData]);
  const p99TrendData = useMemo(() => withRollingTrend(p99Data, 10), [p99Data]);
  const errTrendData = useMemo(() => withRollingTrend(errData, 10), [errData]);
  const memTrendData = useMemo(() => withRollingTrend(memData, 10), [memData]);

  // Apache access-log trend data is already delivered in ms (the backend multiplies
  // @ApacheDuration by 1000 before serialising), so no extra scaling in tooltips.
  const apacheApiAvgData = useMemo(
    () => mergeDualSeries(series?.apacheApiAvg?.colo, series?.apacheApiAvg?.azure),
    [series]
  );
  const apacheUiAvgData = useMemo(
    () => mergeDualSeries(series?.apacheUiAvg?.colo, series?.apacheUiAvg?.azure),
    [series]
  );
  const apacheApiAvgTrendData = useMemo(() => withRollingTrend(apacheApiAvgData, 4), [apacheApiAvgData]);
  const apacheUiAvgTrendData = useMemo(() => withRollingTrend(apacheUiAvgData, 4), [apacheUiAvgData]);

  // Detect whether Azure performance is recovering (last 7 days better than earlier).
  // Uses series data already loaded — no extra API call.
  const recoveryTrend = useMemo(() => {
    const THRESHOLD = 8;
    const improvements = [
      { key: 'p99',      pct: seriesTrendImprovement(p99TrendData,          true)  },
      { key: 'err',      pct: seriesTrendImprovement(errTrendData,           true)  },
      { key: 'apacheUi', pct: seriesTrendImprovement(apacheUiAvgTrendData,   true)  },
      { key: 'apacheApi',pct: seriesTrendImprovement(apacheApiAvgTrendData,  true)  },
    ].filter((d) => d.pct != null);

    if (improvements.length < 2) return null;

    const improving = improvements.filter((d) => d.pct >= THRESHOLD).length;
    const degrading = improvements.filter((d) => d.pct <= -THRESHOLD).length;

    return {
      direction: improving >= 2 ? 'recovering' : degrading >= 2 ? 'degrading' : 'stable',
      improving,
      degrading,
      total: improvements.length,
    };
  }, [p99TrendData, errTrendData, apacheUiAvgTrendData, apacheApiAvgTrendData]);

  /**
   * Build the Apache comparison table rows for a given split (api / ui). Keeping
   * this as a pure memo hook (rather than rendering inline) keeps the JSX readable
   * and ensures the delta / verdict computation matches how `scorecardItems` and
   * `comparisonTableRows` handle the main APM metrics — one mental model for the
   * whole report.
   */
  const apacheRowsFor = (split) => {
    if (!apache || !apache[split]) return [];
    const c = apache[split].colo || {};
    const a = apache[split].azure || {};
    const msFmt = (v) => (v != null && Number.isFinite(v) ? `${v.toFixed(1)} ms` : '—');
    const intFmt = (v) => (v != null && Number.isFinite(v) ? Math.round(v).toLocaleString() : '—');
    const rows = [
      { label: 'Requests (count)', colo: c.count, azure: a.count, lowerBetter: false, fmt: intFmt },
      { label: 'Avg response time', colo: c.avgMs, azure: a.avgMs, lowerBetter: true, fmt: msFmt },
      { label: 'p50 (median)', colo: c.p50Ms, azure: a.p50Ms, lowerBetter: true, fmt: msFmt },
      { label: 'p95 (tail)', colo: c.p95Ms, azure: a.p95Ms, lowerBetter: true, fmt: msFmt },
      { label: 'p99 (worst 1%)', colo: c.p99Ms, azure: a.p99Ms, lowerBetter: true, fmt: msFmt }
    ];
    return rows.map((row) => {
      const d = pctDelta(row.colo, row.azure);
      const verdict = row.lowerBetter
        ? verdictLowerBetter(row.colo, row.azure)
        : verdictHigherBetter(row.colo, row.azure);
      return { ...row, deltaPct: d, verdict };
    });
  };
  const apacheApiRows = useMemo(() => apacheRowsFor('api'), [apache]);
  const apacheUiRows = useMemo(() => apacheRowsFor('ui'), [apache]);


  const scorecardItems = useMemo(() => {
    if (!summary) return [];
    const c = summary.colo;
    const a = summary.azure;
    const fmtInt = (n) => (n != null && Number.isFinite(n) ? n.toLocaleString() : '—');
    const apiC = apache?.api?.colo || {};
    const apiA = apache?.api?.azure || {};
    const uiC = apache?.ui?.colo || {};
    const uiA = apache?.ui?.azure || {};
    const raw = [
      { key: 'p99', title: 'App response time — worst 1% (p99)', colo: c.servletP99Seconds, azure: a.servletP99Seconds, lowerBetter: true, mult: 1000, su: 'ms' },
      { key: 'http', title: 'Outbound connections — avg time', colo: c.httpSeconds, azure: a.httpSeconds, lowerBetter: true, mult: 1000, su: 'ms' },
      {
        key: 'apacheApi',
        title: 'API response time — avg',
        colo: apiC.avgMs != null ? apiC.avgMs / 1000 : null,
        azure: apiA.avgMs != null ? apiA.avgMs / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms',
        // Real-user edge latency naturally varies ~5-10% between envs from client-side
        // network / TLS / proxy differences that aren't true regressions. A <10% gap is
        // workable per product guidance; tighter than that gives too many false positives.
        similarBandPct: 10,
        subColo: apiC.count != null ? `${apiC.count.toLocaleString()} req` : null,
        subAzure: apiA.count != null ? `${apiA.count.toLocaleString()} req` : null
      },
      {
        key: 'apacheApiP95',
        title: 'API response time — p95',
        colo: apiC.p95Ms != null ? apiC.p95Ms / 1000 : null,
        azure: apiA.p95Ms != null ? apiA.p95Ms / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms',
        similarBandPct: 10
      },
      {
        key: 'apacheApiP99',
        title: 'API response time — p99 (worst 1%)',
        colo: apiC.p99Ms != null ? apiC.p99Ms / 1000 : null,
        azure: apiA.p99Ms != null ? apiA.p99Ms / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms',
        similarBandPct: 10
      },
      {
        key: 'apacheUi',
        title: 'UI response time — avg',
        colo: uiC.avgMs != null ? uiC.avgMs / 1000 : null,
        azure: uiA.avgMs != null ? uiA.avgMs / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms',
        subColo: uiC.count != null ? `${uiC.count.toLocaleString()} req` : null,
        subAzure: uiA.count != null ? `${uiA.count.toLocaleString()} req` : null
      },
      {
        key: 'apacheUiP95',
        title: 'UI response time — p95',
        colo: uiC.p95Ms != null ? uiC.p95Ms / 1000 : null,
        azure: uiA.p95Ms != null ? uiA.p95Ms / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms'
      },
      {
        key: 'apacheUiP99',
        title: 'UI response time — p99 (worst 1%)',
        colo: uiC.p99Ms != null ? uiC.p99Ms / 1000 : null,
        azure: uiA.p99Ms != null ? uiA.p99Ms / 1000 : null,
        lowerBetter: true,
        mult: 1000,
        su: 'ms'
      },
      {
        key: 'err',
        title: 'Errors per day (avg)',
        colo: c.errorsPerDay,
        azure: a.errorsPerDay,
        lowerBetter: true,
        mult: 1,
        su: '/day',
        subColo: [
          c.errorRatePct != null ? `${c.errorRatePct.toFixed(3)}% daily avg rate` : null,
          c.activeDays != null ? `${c.activeDays} active days` : null,
          c.totalErrors != null ? `${fmtInt(Math.round(c.totalErrors))} total errors` : null
        ].filter(Boolean).join(' · ') || null,
        subAzure: [
          a.errorRatePct != null ? `${a.errorRatePct.toFixed(3)}% daily avg rate` : null,
          a.activeDays != null ? `${a.activeDays} active days` : null,
          a.totalErrors != null ? `${fmtInt(Math.round(a.totalErrors))} total errors` : null
        ].filter(Boolean).join(' · ') || null
      },
      { key: 'mem', title: 'Server memory available (%)', colo: c.memPctUsable, azure: a.memPctUsable, lowerBetter: false, mult: 1, su: '%' }
    ];
    return raw.map((it) => {
      const d = pctDelta(it.colo, it.azure);
      const verdict = it.lowerBetter
        ? verdictLowerBetter(it.colo, it.azure, it.similarBandPct)
        : verdictHigherBetter(it.colo, it.azure, it.similarBandPct);
      const fmt = (v) => {
        if (v == null || !Number.isFinite(v)) return '—';
        const x = v * it.mult;
        if (it.su === 'ms') return `${x.toFixed(1)} ms`;
        if (it.su === '/day') return `${Math.round(x).toLocaleString()}/day`;
        return `${x.toFixed(2)}%`;
      };
      return { ...it, deltaPct: d, verdict, fmtColo: fmt(it.colo), fmtAzure: fmt(it.azure) };
    });
  }, [summary, apache]);

  const migrationTs = meta?.azureMigrationDate ? new Date(meta.azureMigrationDate).getTime() : null;
  const rangeStart = meta?.from ? new Date(meta.from).getTime() : null;
  const rangeEnd = meta?.to ? new Date(meta.to).getTime() : null;
  const showMigrationLine =
    migrationTs != null &&
    rangeStart != null &&
    rangeEnd != null &&
    migrationTs >= rangeStart &&
    migrationTs <= rangeEnd;

  const comparisonTableRows = useMemo(() => {
    if (!summary) return [];
    const c = summary.colo;
    const a = summary.azure;
    const msFmt = (v) => (v != null && Number.isFinite(v) ? `${(v * 1000).toFixed(1)} ms` : '—');
    const rows = [
      {
        label: 'App response time — p99 worst-case',
        colo: c.servletP99Seconds,
        azure: a.servletP99Seconds,
        lowerBetter: true,
        fmt: msFmt
      },
      {
        label: 'Outbound connections — average time',
        colo: c.httpSeconds,
        azure: a.httpSeconds,
        lowerBetter: true,
        fmt: msFmt
      },
      {
        label: 'Errors per day (avg, active days only)',
        colo: c.errorsPerDay,
        azure: a.errorsPerDay,
        lowerBetter: true,
        fmt: (v) => (v != null && Number.isFinite(v) ? `${Math.round(v).toLocaleString()}/day` : '—')
      },
      {
        label: 'Error rate (mean of daily rates)',
        colo: c.errorRatePct,
        azure: a.errorRatePct,
        lowerBetter: true,
        fmt: (v) => (v != null && Number.isFinite(v) ? `${v.toFixed(3)}%` : '—')
      },
      {
        label: 'Active days (Colo / Azure)',
        colo: c.activeDays,
        azure: a.activeDays,
        lowerBetter: null,
        fmt: (v) => (v != null && Number.isFinite(v) ? `${v} days` : '—')
      },
      {
        label: 'Total errors over window',
        colo: c.totalErrors,
        azure: a.totalErrors,
        lowerBetter: true,
        fmt: (v) => (v != null && Number.isFinite(v) ? v.toLocaleString() : '—')
      },
      {
        label: 'Total hits over window',
        colo: c.totalHits,
        azure: a.totalHits,
        lowerBetter: false,
        fmt: (v) => (v != null && Number.isFinite(v) ? v.toLocaleString() : '—')
      },
      {
        label: 'Server memory available % (avg)',
        colo: c.memPctUsable,
        azure: a.memPctUsable,
        lowerBetter: false,
        fmt: (v) => (v != null && Number.isFinite(v) ? `${v.toFixed(2)}%` : '—')
      }
    ];
    return rows.map((row) => {
      const d = pctDelta(row.colo, row.azure);
      const verdict = row.lowerBetter === null
        ? { tone: 'neutral', label: 'info' }
        : row.lowerBetter
          ? verdictLowerBetter(row.colo, row.azure)
          : verdictHigherBetter(row.colo, row.azure);
      return { ...row, deltaPct: d, verdict };
    });
  }, [summary]);

  const weeklyStats = useMemo(() => computeWeeklyStats({ series, meta }), [series, meta]);
  const rootCauseHypotheses = useMemo(
    () => computeRootCauseHypotheses({ weekly: weeklyStats, summary, topErrors, meta }),
    [weeklyStats, summary, topErrors, meta]
  );

  const uiResponseRows  = useMemo(() => buildResponseRows('ui',  apache), [apache]);
  const apiResponseRows = useMemo(() => buildResponseRows('api', apache), [apache]);

  const activeTrendData = useMemo(() => {
    switch (trendMetricKey) {
      case 'http': return httpTrendData;
      case 'err':  return errTrendData;
      case 'mem':  return memTrendData;
      default:     return p99TrendData;
    }
  }, [trendMetricKey, p99TrendData, httpTrendData, errTrendData, memTrendData]);

  const activeTrendYFmt = useMemo(() => {
    switch (trendMetricKey) {
      case 'err': return (v) => `${v.toFixed(2)}%`;
      case 'mem': return (v) => `${v.toFixed(1)}%`;
      default:    return (v) => `${(v * 1000).toFixed(0)} ms`;
    }
  }, [trendMetricKey]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo
            options={recentInstances}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.instanceName ?? ''
            }
            inputValue={clientQuery}
            onInputChange={(_, val) => setClientQuery(val)}
            onChange={(_, val) => {
              if (val && typeof val === 'object') {
                const name = val.instanceName ?? '';
                setClientQuery(name);
                runReport({ queryOverride: name });
              }
            }}
            loading={instancesLoading}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return q
                ? opts.filter(
                    (o) =>
                      o.instanceName?.toLowerCase().includes(q) ||
                      o.instanceUrl?.toLowerCase().includes(q)
                  )
                : opts;
            }}
            renderOption={(props, opt) => {
              const daysAgo = opt.azureMigrationDate
                ? formatDistanceToNow(new Date(opt.azureMigrationDate), { addSuffix: true })
                : null;
              const meta = [
                daysAgo ? `migrated ${daysAgo}` : null,
                opt.currentDatacenter || null,
              ].filter(Boolean).join(' · ');
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '5px !important', minHeight: 44 }}
                >
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.4, maxWidth: '100%' }}>
                    {opt.instanceName}
                  </Typography>
                  {meta && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3, maxWidth: '100%' }}>
                      {meta}
                    </Typography>
                  )}
                </Box>
              );
            }}
            noOptionsText={
              instancesLoading
                ? 'Loading recent migrations…'
                : recentInstances.length === 0
                  ? 'No migrations found in the last 30 days'
                  : 'No match — type to search all instances'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client / instance name"
                placeholder="Search or type instance name"
                size="small"
                onKeyDown={(e) => e.key === 'Enter' && load()}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {instancesLoading && <CircularProgress color="inherit" size={14} sx={{ mr: 1 }} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={load}
            disabled={loading}
            sx={{ bgcolor: AZURE_COLOR, '&:hover': { bgcolor: '#163a2c' }, height: 40 }}
          >
            {loading ? 'Loading…' : 'Run report'}
          </Button>
        </Grid>
        <Grid item xs={12} md={3}>
          <MuiTooltip title={!payload ? 'Run a report first' : 'Download a standalone HTML file you can open, share, or print'}>
            <span style={{ display: 'block' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                disabled={!payload || loading}
                onClick={() => {
                  const html = generateReportHtml({
                    meta,
                    summary,
                    scorecardItems,
                    rootCauseHypotheses,
                    topErrors,
                    uiResponseRows,
                    apiResponseRows,
                  });
                  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const safeName = (meta?.instanceName || 'report').replace(/[^a-z0-9]/gi, '_').toLowerCase();
                  a.href = url;
                  a.download = `migration_report_${safeName}.html`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                sx={{ height: 40 }}
              >
                Export to HTML
              </Button>
            </span>
          </MuiTooltip>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {ignoredMeta && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderLeft: '4px solid', borderColor: 'text.disabled' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {ignoredMeta.instanceName || clientQuery}
                </Typography>
                <Chip label="Ignored" size="small" sx={{ bgcolor: '#f5f5f5', color: '#616161', fontWeight: 700 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Source and target datacenter are the same —{' '}
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>
                  {ignoredMeta.oldEnv}
                </Box>
                {ignoredMeta.sourceDatacenter && ignoredMeta.sourceDatacenter !== ignoredMeta.oldEnv &&
                  ` (${ignoredMeta.sourceDatacenter})`}
                . No Colo vs Azure comparison is possible for this client.
              </Typography>
              <Typography variant="caption" color="text.disabled">
                To run a report, ensure the migration sheet has different Source and Current datacenter values,
                or override them in Edit parameters.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {meta && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {meta.instanceName || '—'}
              </Typography>
              {meta.instanceUrl && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {meta.instanceUrl}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ px: 1, py: 0.25, borderRadius: 1, fontWeight: 700, fontSize: 12, bgcolor: '#fde8df', color: COLO_COLOR }}>
                  Before: {meta.oldEnv}
                </Box>
                <Typography variant="caption" color="text.disabled">→</Typography>
                <Box sx={{ px: 1, py: 0.25, borderRadius: 1, fontWeight: 700, fontSize: 12, bgcolor: '#d4eadf', color: AZURE_COLOR }}>
                  After: {meta.newEnv}
                </Box>
                {meta.azureMigrationDate && (
                  <Chip
                    size="small"
                    label={`Migrated ${format(new Date(meta.azureMigrationDate), 'd MMM yyyy')}`}
                    sx={{ fontWeight: 600, height: 22, fontSize: 11 }}
                  />
                )}
                {meta.azureMigrationDate && (
                  <Chip
                    size="small"
                    label={`${Math.floor((Date.now() - new Date(meta.azureMigrationDate).getTime()) / 86400000)} days on Azure`}
                    sx={{ fontWeight: 600, height: 22, fontSize: 11, bgcolor: AZURE_COLOR, color: '#fff' }}
                  />
                )}
              </Box>
              {payload?.exclusions?.apmResourceNames?.length ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Internal health-check paths excluded from calculations:{' '}
                  {payload.exclusions.apmResourceNames.map((r, i) => (
                    <React.Fragment key={r}>
                      {i > 0 ? ', ' : ''}
                      <code>{r}</code>
                    </React.Fragment>
                  ))}
                </Typography>
              ) : null}
            </Box>
            <MuiTooltip title={editOpen ? 'Hide parameters' : 'Edit source DC, target DC, migration date or service'}>
              <Button
                variant={editOpen ? 'contained' : 'outlined'}
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen((v) => !v)}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {editOpen ? 'Done' : 'Edit parameters'}
              </Button>
            </MuiTooltip>
          </Box>

          <Collapse in={editOpen} unmountOnExit>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Values below were auto-picked from the migration sheet. Tweak any of them and
                click <strong>Reload</strong> to re-run the report.
              </Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source datacenter (Before)"
                    placeholder="e.g. usa1"
                    value={oldEnv}
                    onChange={(e) => setOldEnv(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Target datacenter (After)"
                    placeholder="e.g. zus1"
                    value={newEnv}
                    onChange={(e) => setNewEnv(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Migration date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={migrationDateInput}
                    onChange={(e) => setMigrationDateInput(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Client name (Datadog service tag)"
                    placeholder={meta?.clientName || 'e.g. bowmans'}
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    helperText="Leave blank to auto-detect."
                  />
                </Grid>
                {/* ── Resource name exclusions ── */}
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Exclude resource names from error rate
                    </Typography>
                    <MuiTooltip title="Edit global exclusion list — applies to all clients including Weekly Cohort" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setModalDraft([...excludedResources]);
                          setModalInput('');
                          setModalError(null);
                          setExclusionModalOpen(true);
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                  </Stack>
                  {excludedResources.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={0.75}>
                      {excludedResources.map((r) => (
                        <Chip
                          key={r}
                          label={r}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      None — click the edit icon to add exclusions that apply globally across all clients.
                    </Typography>
                  )}
                </Grid>

                {/* ── Day exclusions ── */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                    Exclude specific days from all metric calculations
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Useful for incident days, deployment spikes, or other known anomalies. Excluded days are dropped from
                    error rate, p99 latency, CPU, and memory averages. The cutover day is always excluded automatically.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: excludedDays.length ? 1 : 0 }}>
                    <TextField
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      label="Date to exclude"
                      value={excludeDayInput}
                      onChange={(e) => setExcludeDayInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExcludeDay(); } }}
                      sx={{ width: 200 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={addExcludeDay}
                      disabled={!excludeDayInput.trim()}
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                      Add &amp; Reload
                    </Button>
                  </Stack>
                  {excludedDays.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.75}>
                      {excludedDays.map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          size="small"
                          onDelete={() => removeExcludeDay(d)}
                          color="error"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* ── Weekdays only ── */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={weekdaysOnly}
                        onChange={(e) => {
                          setWeekdaysOnly(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="caption" fontWeight={600}>
                        Weekdays only — exclude Saturdays &amp; Sundays from all metric calculations
                      </Typography>
                    }
                  />
                  {weekdaysOnly && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, ml: 5 }}>
                      All weekend dates in the 30-day window are automatically added to the day exclusion list, giving a
                      cleaner baseline free of weekend traffic patterns.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={load}
                      disabled={loading}
                      sx={{ bgcolor: AZURE_COLOR, '&:hover': { bgcolor: '#163a2c' }, textTransform: 'none' }}
                    >
                      {loading ? 'Reloading…' : 'Reload with these values'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RestartAltIcon />}
                      onClick={resetOverridesAndReload}
                      disabled={loading}
                      sx={{ textTransform: 'none' }}
                    >
                      Reset to sheet values
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Collapse>

          {!meta.azureMigrated && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This instance is not marked as Azure-migrated in the sheet; metrics still reflect env tags above.
            </Alert>
          )}
        </Paper>
      )}


      {/* ── Migration verdict + health cards ── */}
      {scorecardItems.length > 0 && meta && (
        <>
          <MigrationVerdictBanner scorecardItems={scorecardItems} meta={meta} recoveryTrend={recoveryTrend} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <HealthCard
                label="Is it fast?"
                question="How quickly are pages loading for users?"
                item={scorecardItems.find((i) => i.key === 'apacheUi') || scorecardItems.find((i) => i.key === 'p99')}
                meta={meta}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <HealthCard
                label="Is it reliable?"
                question="How many requests are failing with errors?"
                item={scorecardItems.find((i) => i.key === 'err')}
                meta={meta}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <HealthCard
                label="Is the server healthy?"
                question="How much free memory does the server have?"
                item={scorecardItems.find((i) => i.key === 'mem')}
                meta={meta}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* ── Response times table ── */}
      {apache && meta && (uiResponseRows.length > 0 || apiResponseRows.length > 0) && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: AZURE_COLOR, mb: 0.5 }}>
              Response times
            </Typography>
            <Typography variant="body2" color="text.secondary">
              How long users wait for the app — measured at the server's front door, from before ({meta.oldEnv}) to after ({meta.newEnv}) the migration.
            </Typography>
          </Box>
          <Tabs
            value={activeResponseTab}
            onChange={(_, v) => setActiveResponseTab(v)}
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Website visitors" value="ui" />
            <Tab label="Integrations & API" value="api" />
          </Tabs>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>What we measure</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: COLO_COLOR, whiteSpace: 'nowrap' }}>
                      Before ({meta.oldEnv})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: AZURE_COLOR, whiteSpace: 'nowrap' }}>
                      After ({meta.newEnv})
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(activeResponseTab === 'ui' ? uiResponseRows : apiResponseRows).map((row) => {
                    const isGood = row.verdict.tone === 'good';
                    const isBad  = row.verdict.tone === 'bad';
                    return (
                      <TableRow key={row.label}>
                        <TableCell sx={{ py: 1 }}>
                          <MuiTooltip title={row.tooltip} arrow placement="right">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                              <Typography variant="body2">{row.label}</Typography>
                              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            </Box>
                          </MuiTooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: COLO_COLOR }}>
                            {row.fmt(row.before)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: AZURE_COLOR }}>
                            {row.fmt(row.after)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {row.deltaPct != null && Number.isFinite(row.deltaPct) ? (
                            <Box
                              sx={{
                                display: 'inline-block', px: 0.75, py: 0.2, borderRadius: 0.75,
                                bgcolor: isGood ? '#e8f5e9' : isBad ? '#ffebee' : '#eeeeee',
                                color: isGood ? GOOD : isBad ? BAD : '#555',
                                fontWeight: 700, fontSize: 12
                              }}
                            >
                              {row.deltaPct > 0 ? '+' : ''}{row.deltaPct.toFixed(1)}%
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {apache.note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 1.5 }}>
              {apache.note}
            </Typography>
          )}
        </Paper>
      )}

      {(p99TrendData.length > 0 || errTrendData.length > 0) && meta && (
        <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          <Typography variant="overline" color="text.disabled" sx={{ fontSize: 10, letterSpacing: 1.5, whiteSpace: 'nowrap' }}>
            Performance over time
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
        </Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: AZURE_COLOR }}>
              Performance over time — before vs after migration
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 2, bgcolor: COLO_COLOR, borderRadius: 1, opacity: 0.8, borderTop: '2px dashed', borderColor: COLO_COLOR }} />
                <Typography variant="caption" color="text.secondary">Before ({meta.oldEnv})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 20, height: 2, bgcolor: AZURE_COLOR, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">After ({meta.newEnv})</Typography>
              </Box>
            </Box>
          </Box>
          <Grid container spacing={2}>
            {[
              { label: 'App response time (p99)', data: p99TrendData, tooltip: <DurationTooltip />, yFmt: (v) => `${(v * 1000).toFixed(0)}ms`, note: 'slowest 1% of requests — APM' },
              { label: 'Error rate', data: errTrendData, tooltip: <GenericTooltip valueLabel={(v) => `${v.toFixed(3)}%`} />, yFmt: (v) => `${v.toFixed(2)}%`, note: '% requests failing daily avg' },
              { label: 'Server memory free', data: memTrendData, tooltip: <GenericTooltip valueLabel={(v) => `${v.toFixed(1)}%`} />, yFmt: (v) => `${v.toFixed(0)}%`, note: '% RAM available — higher is better' },
              { label: 'Apache response time (UI)', data: apacheUiAvgTrendData, tooltip: <DurationTooltip />, yFmt: (v) => `${v.toFixed(0)}ms`, note: 'real user page load at edge' },
            ].map(({ label, data, tooltip, yFmt, note }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{note}</Typography>
                </Box>
                <Box sx={{ width: '100%', height: 190 }}>
                  <ResponsiveContainer>
                    <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={formatTickTs} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 9 }} width={44} tickFormatter={yFmt} tickLine={false} axisLine={false} />
                      <Tooltip content={tooltip} />
                      {showMigrationLine && (
                        <ReferenceLine x={migrationTs} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1.5}
                          label={{ value: '↑ migrated', position: 'insideTopLeft', fill: '#b45309', fontSize: 9 }} />
                      )}
                      <Line type="monotone" dataKey="colo" stroke={COLO_COLOR} strokeDasharray="5 3" strokeWidth={2} dot={false} connectNulls />
                      <Line type="monotone" dataKey="azure" stroke={AZURE_COLOR} strokeWidth={2} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
        </>
      )}

      {(topErrors || topErrors?.comparison) && meta && (
        <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          <Typography variant="overline" color="text.disabled" sx={{ fontSize: 10, letterSpacing: 1.5, whiteSpace: 'nowrap' }}>
            Error changes
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
        </Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: AZURE_COLOR, mb: 0.5 }}>
            Did the number of errors change?
          </Typography>
          {summary && (
            <Grid container spacing={2} sx={{ mb: 2, mt: 0.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderLeft: 4, borderColor: COLO_COLOR }}>
                  <Typography variant="caption" color="text.secondary" display="block">Before — errors per day</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: COLO_COLOR }}>
                    {summary.colo.errorsPerDay != null ? Math.round(summary.colo.errorsPerDay).toLocaleString() : '—'}
                  </Typography>
                  {summary.colo.errorRatePct != null && (
                    <Typography variant="caption" color="text.secondary">{summary.colo.errorRatePct.toFixed(3)}% rate</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderLeft: 4, borderColor: AZURE_COLOR }}>
                  <Typography variant="caption" color="text.secondary" display="block">After — errors per day</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: AZURE_COLOR }}>
                    {summary.azure.errorsPerDay != null ? Math.round(summary.azure.errorsPerDay).toLocaleString() : '—'}
                  </Typography>
                  {summary.azure.errorRatePct != null && (
                    <Typography variant="caption" color="text.secondary">{summary.azure.errorRatePct.toFixed(3)}% rate</Typography>
                  )}
                </Paper>
              </Grid>
              {(() => {
                const errItem = scorecardItems.find((it) => it.key === 'err');
                if (!errItem || errItem.deltaPct == null || !Number.isFinite(errItem.deltaPct)) return null;
                const isGood = errItem.verdict.tone === 'good';
                const isBad = errItem.verdict.tone === 'bad';
                return (
                  <Grid item xs={12} sm={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center',
                      bgcolor: isGood ? '#f0faf0' : isBad ? '#fff5f5' : '#fafafa',
                      borderLeft: 4, borderColor: isGood ? GOOD : isBad ? BAD : '#ccc' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Change after migration</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: isGood ? GOOD : isBad ? BAD : '#555' }}>
                        {errItem.deltaPct > 0 ? '+' : ''}{errItem.deltaPct.toFixed(1)}%
                      </Typography>
                      <Chip size="small" label={errItem.verdict.label}
                        sx={{ bgcolor: isGood ? 'success.light' : isBad ? 'error.light' : 'action.selected', fontWeight: 600, mt: 0.5 }} />
                    </Paper>
                  </Grid>
                );
              })()}
            </Grid>
          )}
          {topErrors?.comparison && (
            <Grid container spacing={2}>
              {/* Concise new/cleared summary row */}
              {(topErrors.comparison.newInAzureByResource?.length > 0 || topErrors.comparison.clearedInAzureByResource?.length > 0) && (
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {topErrors.comparison.newInAzureByResource?.length > 0 && (
                      <Chip
                        size="small"
                        label={`${topErrors.comparison.newInAzureByResource.length} new error pattern${topErrors.comparison.newInAzureByResource.length !== 1 ? 's' : ''} on Azure`}
                        sx={{ bgcolor: 'error.light', color: BAD, fontWeight: 600, fontSize: 11 }}
                      />
                    )}
                    {topErrors.comparison.clearedInAzureByResource?.length > 0 && (
                      <Chip
                        size="small"
                        label={`${topErrors.comparison.clearedInAzureByResource.length} pattern${topErrors.comparison.clearedInAzureByResource.length !== 1 ? 's' : ''} resolved`}
                        sx={{ bgcolor: 'success.light', color: GOOD, fontWeight: 600, fontSize: 11 }}
                      />
                    )}
                    {meta?.service && meta?.newEnv && (
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                        href={buildDdTracesUrl({ service: meta.service, env: meta.newEnv, fromMs: rangeStart, toMs: rangeEnd })}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', fontSize: 11, px: 0.5, color: 'text.secondary' }}
                      >
                        View all in Datadog
                      </Button>
                    )}
                  </Stack>
                </Grid>
              )}
              {/* Top newly introduced errors by frequency */}
              {topErrors.comparison.topNewInAzureByResource?.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid', borderColor: 'error.light', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: '#fff0f0', borderBottom: '1px solid', borderColor: 'error.light', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ color: BAD, fontWeight: 700 }}>
                        Top {topErrors.comparison.topNewInAzureByResource.length} newly introduced errors — by frequency
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        (post-migration only, sorted by Azure error count)
                      </Typography>
                      {meta?.service && meta?.newEnv && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                          href={buildDdTracesUrl({ service: meta.service, env: meta.newEnv, fromMs: rangeStart, toMs: rangeEnd })}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: 'none', fontSize: 11, py: 0.25, px: 1 }}
                        >
                          View all Azure errors in Datadog
                        </Button>
                      )}
                    </Box>
                    <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fff8f8' }}>
                          <TableCell sx={{ width: 36, fontWeight: 700, color: 'text.secondary', fontSize: 11 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11 }}>Resource</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, whiteSpace: 'nowrap' }}>Errors (Azure)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, whiteSpace: 'nowrap' }}>% of Azure errors</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, whiteSpace: 'nowrap' }}>Investigate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topErrors.comparison.topNewInAzureByResource.map((r, i) => (
                          <TableRow key={r.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ color: 'text.disabled', fontWeight: 700, fontSize: 12 }}>{i + 1}</TableCell>
                            <TableCell>
                              <MuiTooltip title={r.label} arrow>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: BAD, maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {r.label}
                                </Typography>
                              </MuiTooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: BAD, fontSize: 13 }}>
                                {r.count.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                size="small"
                                label={r.pctOfErrors != null ? `${r.pctOfErrors}%` : '—'}
                                sx={{ bgcolor: 'error.light', color: BAD, fontWeight: 700, fontSize: 11, height: 20 }}
                              />
                            </TableCell>
                            <TableCell>
                              {meta?.service && meta?.newEnv && (
                                <Button
                                  size="small"
                                  variant="text"
                                  color="error"
                                  endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                                  href={buildDdTracesUrl({ service: meta.service, env: meta.newEnv, fromMs: rangeStart, toMs: rangeEnd, resource: r.label })}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: 'none', fontSize: 11, py: 0, px: 0.5, minWidth: 0 }}
                                >
                                  Traces
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              )}

              {topErrors.comparison.caveat && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>{topErrors.comparison.caveat}</Alert>
                </Grid>
              )}

            </Grid>
          )}
        </Paper>
        </>
      )}

      {rootCauseHypotheses.length > 0 && meta && (
        <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          <Typography variant="overline" color="text.disabled" sx={{ fontSize: 10, letterSpacing: 1.5, whiteSpace: 'nowrap' }}>
            Root cause insights
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
        </Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: AZURE_COLOR }}>
              Why did these changes happen?
            </Typography>
            {rootCauseHypotheses.some((h) => h.tone === 'bad') && (
              <Chip size="small" label="needs attention"
                sx={{ fontWeight: 600, bgcolor: 'error.light', color: BAD, height: 20, fontSize: 11 }} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Automated analysis highlights likely causes by comparing signals across speed, errors, and memory week over week.
            Each card shows what the data suggests and where to look next.
          </Typography>
          <Grid container spacing={2}>
            {rootCauseHypotheses.map((h) => {
              const chipColor = h.tone === 'bad' ? 'error.light' : h.tone === 'watch' ? 'warning.light' : 'success.light';
              const borderColor = h.tone === 'bad' ? BAD : h.tone === 'watch' ? '#ed6c02' : h.tone === 'neutral' ? '#6c757d' : AZURE_COLOR;
              return (
                <Grid item xs={12} md={6} key={h.id}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderLeft: 4, borderColor }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>{h.label}</Typography>
                      <Chip size="small" label={`${h.confidence} confidence`}
                        sx={{ fontWeight: 500, textTransform: 'capitalize', bgcolor: chipColor }} />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>{h.summary}</Typography>
                    {h.evidence?.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Evidence</Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1.5 }}>
                          {h.evidence.map((e, i) => (
                            <Typography key={`${h.id}-e-${i}`} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>{e}</Typography>
                          ))}
                        </Box>
                      </>
                    )}
                    {h.nextSteps?.length > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Where to look next</Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                          {h.nextSteps.map((s, i) => (
                            <Typography key={`${h.id}-s-${i}`} component="li" variant="body2" sx={{ mb: 0.25 }}>{s}</Typography>
                          ))}
                        </Box>
                      </>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
        </>
      )}

      {(series || (summary && meta)) && (
        <Accordion
          disableGutters
          variant="outlined"
          sx={{ mb: 3, '&:before': { display: 'none' }, borderRadius: '4px !important' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: 'action.hover',
              borderRadius: '4px',
              minHeight: 48,
              '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 0 }
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: AZURE_COLOR }}>
              Deep dive — detailed charts and raw data
            </Typography>
            <Chip
              size="small"
              label="for engineers"
              sx={{ height: 20, fontSize: 10, bgcolor: 'action.selected', fontWeight: 600 }}
            />
          </AccordionSummary>

          <AccordionDetails sx={{ p: 2 }}>

          {scorecardItems.length > 0 && meta && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
                All metrics — click a row to see causes &amp; Datadog checks
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Metric</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: COLO_COLOR }}>Before ({meta.oldEnv})</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>Δ</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: AZURE_COLOR }}>After ({meta.newEnv})</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>Verdict</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scorecardItems.map((it) => {
                      const isSelected = selectedScorecardKey === it.key;
                      const isGood = it.verdict.tone === 'good';
                      const isBad  = it.verdict.tone === 'bad';
                      return (
                        <TableRow
                          key={it.key}
                          hover
                          onClick={() => setSelectedScorecardKey((prev) => (prev === it.key ? null : it.key))}
                          sx={{ cursor: 'pointer', bgcolor: isSelected ? 'action.selected' : undefined }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedScorecardKey((prev) => (prev === it.key ? null : it.key));
                            }
                          }}
                        >
                          <TableCell sx={{ py: 1 }}>
                            <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>{it.title}</Typography>
                            {it.subColo && <Typography variant="caption" color="text.secondary" display="block">{it.subColo}</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: COLO_COLOR }}>{it.fmtColo}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {it.deltaPct != null && Number.isFinite(it.deltaPct) ? (
                              <Box sx={{ display: 'inline-block', px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: isGood ? '#e8f5e9' : isBad ? '#ffebee' : '#eeeeee', color: isGood ? GOOD : isBad ? BAD : '#555', fontWeight: 700, fontSize: 12 }}>
                                {it.deltaPct > 0 ? '+' : ''}{it.deltaPct.toFixed(1)}%
                              </Box>
                            ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: AZURE_COLOR }}>{it.fmtAzure}</Typography>
                            {it.subAzure && <Typography variant="caption" color="text.secondary" display="block">{it.subAzure}</Typography>}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            <Chip size="small" label={it.verdict.label} sx={{ fontWeight: 600, height: 20, fontSize: 10, bgcolor: isGood ? 'success.light' : isBad ? 'error.light' : 'action.selected', color: isGood ? GOOD : isBad ? BAD : 'text.secondary' }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedScorecardKey && (
                <Box sx={{ mt: 2 }}>
                  <ScorecardDetailPanel
                    selectedKey={selectedScorecardKey}
                    scorecardItems={scorecardItems}
                    meta={meta}
                    onClose={() => setSelectedScorecardKey(null)}
                  />
                </Box>
              )}
            </Box>
          )}

          {summary && meta && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
                Raw period data — full metric table
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric (period average)</TableCell>
                      <TableCell sx={{ color: COLO_COLOR, fontWeight: 600 }}>Before ({meta.oldEnv})</TableCell>
                      <TableCell sx={{ color: AZURE_COLOR, fontWeight: 600 }}>After ({meta.newEnv})</TableCell>
                      <TableCell align="right">Δ</TableCell>
                      <TableCell>Verdict</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonTableRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.fmt(row.colo)}</TableCell>
                        <TableCell>{row.fmt(row.azure)}</TableCell>
                        <TableCell align="right">
                          {row.deltaPct != null && Number.isFinite(row.deltaPct) ? (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                color:
                                  row.verdict.tone === 'good'
                                    ? GOOD
                                    : row.verdict.tone === 'bad'
                                      ? BAD
                                      : 'text.secondary',
                                fontWeight: 600
                              }}
                            >
                              {row.deltaPct > 0 ? '+' : ''}
                              {row.deltaPct.toFixed(1)}%
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.verdict.label}
                            sx={{
                              fontWeight: 500,
                              bgcolor:
                                row.verdict.tone === 'good'
                                  ? 'success.light'
                                  : row.verdict.tone === 'bad'
                                    ? 'error.light'
                                    : 'action.selected'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={!!selectedPoint} onClose={() => setSelectedPoint(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPoint?.metricLabel || 'Point details'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Timestamp:{' '}
            {selectedPoint?.point?.t != null
              ? format(new Date(selectedPoint.point.t), 'd MMM yyyy, HH:mm')
              : '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: COLO_COLOR, fontWeight: 600 }}>
            Before: {fmtWithUnit(selectedPoint?.point?.colo, selectedPoint?.formatter || ((v) => `${v}`))}
          </Typography>
          <Typography variant="body2" sx={{ color: AZURE_COLOR, fontWeight: 600, mb: 1 }}>
            After: {fmtWithUnit(selectedPoint?.point?.azure, selectedPoint?.formatter || ((v) => `${v}`))}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Change (After − Before):{' '}
            {selectedPoint?.point?.azure != null &&
            Number.isFinite(selectedPoint.point.azure) &&
            selectedPoint?.point?.colo != null &&
            Number.isFinite(selectedPoint.point.colo)
              ? fmtWithUnit(
                  selectedPoint.point.azure - selectedPoint.point.colo,
                  selectedPoint?.formatter || ((v) => `${v}`)
                )
              : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trend — Before: {fmtWithUnit(selectedPoint?.point?.coloTrend, selectedPoint?.formatter || ((v) => `${v}`))}
            {' · '}
            After: {fmtWithUnit(selectedPoint?.point?.azureTrend, selectedPoint?.formatter || ((v) => `${v}`))}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPoint(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Global resource exclusion editor modal ── */}
      <Dialog
        open={exclusionModalOpen}
        onClose={() => setExclusionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Exclude resource names from error rate</Typography>
            <Typography variant="caption" color="text.secondary">
              Applies globally to all clients — including Weekly Cohort. Changes are persisted immediately.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExclusionModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {modalError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setModalError(null)}>
              {modalError}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. post_/_/deletefile.action"
              value={modalInput}
              onChange={(e) => setModalInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const v = modalInput.trim();
                  if (v && !modalDraft.includes(v)) setModalDraft((d) => [...d, v]);
                  setModalInput('');
                }
              }}
              helperText="Press Enter or click Add"
            />
            <Button
              size="small"
              variant="outlined"
              disabled={!modalInput.trim()}
              onClick={() => {
                const v = modalInput.trim();
                if (v && !modalDraft.includes(v)) setModalDraft((d) => [...d, v]);
                setModalInput('');
              }}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Add
            </Button>
          </Stack>
          {modalDraft.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {modalDraft.map((r) => (
                <Chip
                  key={r}
                  label={r}
                  size="small"
                  color="warning"
                  variant="outlined"
                  onDelete={() => setModalDraft((d) => d.filter((x) => x !== r))}
                  sx={{ fontFamily: 'monospace', fontSize: 11 }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No exclusions defined. Add resource names above to exclude them from error rate calculations across all clients.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExclusionModalOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={modalSaving}
            onClick={async () => {
              setModalSaving(true);
              setModalError(null);
              try {
                await updateAzureMigrationExclusionConfig(modalDraft);
                setExcludedResources(modalDraft);
                setExclusionModalOpen(false);
                if (lastLoadedClient) runReport({ exclusionsOverride: modalDraft });
              } catch (err) {
                setModalError(err?.message || 'Failed to save exclusions. Please try again.');
              } finally {
                setModalSaving(false);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {modalSaving ? 'Saving…' : 'Save & Apply'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AzureMigrationPerformanceTab;
